import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ApiURL from '../Api/Api';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { SidebarContext } from './SidebarContext';
import {
  BanknotesIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  TagIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const inputCls = 'w-full h-10 px-3 text-[13px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none transition-all focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100';

const Projects = () => {
  const companyId = localStorage.getItem('company_id') || '';
  const currentUserName = localStorage.getItem('user_name') || 'admin';
  const { isSidebarOpen } = useContext(SidebarContext);
  const [projects, setProjects] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editForm, setEditForm] = useState({ project_name: '', allocated_budget: '' });
  const [form, setForm] = useState({
    project_name: '',
    allocated_budget: '',
    main_head_name: '',
    sub_main_name: '',
  });
  const projectNames = useMemo(
    () => [...new Set(projects.map((project) => project.project_name).filter(Boolean))].sort(),
    [projects]
  );
  const selectedProject = useMemo(
    () => projects.find((project) => project.project_name === form.project_name),
    [projects, form.project_name]
  );

  const mainHeads = useMemo(
    () => [...new Set(attributes.map((attr) => attr.main_head_name).filter(Boolean))].sort(),
    [attributes]
  );
  const subHeads = useMemo(
    () => [
      ...new Set(
        attributes
          .filter((attr) => attr.main_head_name === form.main_head_name)
          .map((attr) => attr.sub_main_name)
          .filter(Boolean)
      ),
    ].sort(),
    [attributes, form.main_head_name]
  );

  const loadProjects = async () => {
    try {
      const response = await fetch(`${ApiURL}/projects.php?company_id=${encodeURIComponent(companyId)}`);
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to load projects');
      }
      setProjects(data.projects || []);
      setAttributes(data.attributes || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load projects');
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const updateForm = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'main_head_name' ? { sub_main_name: '' } : {}),
    }));
  };

  const saveProject = async (event) => {
    event.preventDefault();
    if (!form.project_name.trim() || String(form.allocated_budget).trim() === '') {
      toast.error('Project name and budget are required');
      return;
    }
    if (Number(form.allocated_budget || 0) < 0) {
      toast.error('Allocated budget cannot be negative');
      return;
    }
    if (!form.main_head_name.trim() || !form.sub_main_name.trim()) {
      toast.error('Main head and sub head are required');
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(`${ApiURL}/projects.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          allocated_budget: Number(form.allocated_budget || 0),
          company_id: companyId,
          created_by: currentUserName,
          created_date: new Date().toISOString().split('T')[0],
        }),
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to save project');
      }
      toast.success(data.message || 'Project saved');
      setForm((prev) => ({
        project_name: prev.project_name,
        allocated_budget: prev.allocated_budget,
        main_head_name: '',
        sub_main_name: '',
      }));
      loadProjects();
    } catch (error) {
      toast.error(error.message || 'Failed to save project');
    } finally {
      setIsSaving(false);
    }
  };

  const startEditProject = (project) => {
    setEditingProject(project);
    setEditForm({
      project_name: project.project_name || '',
      allocated_budget: String(project.allocated_budget ?? ''),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEditProject = () => {
    setEditingProject(null);
    setEditForm({ project_name: '', allocated_budget: '' });
  };

  const updateProject = async (event) => {
    event.preventDefault();
    if (!editingProject) return;
    if (!editForm.project_name.trim() || String(editForm.allocated_budget).trim() === '') {
      toast.error('Project name and budget are required');
      return;
    }
    if (Number(editForm.allocated_budget || 0) < 0) {
      toast.error('Allocated budget cannot be negative');
      return;
    }

    setIsUpdating(true);
    try {
      const response = await fetch(`${ApiURL}/projects.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update_project',
          project_id: editingProject.project_id,
          company_id: companyId,
          project_name: editForm.project_name,
          allocated_budget: Number(editForm.allocated_budget || 0),
          updated_by: currentUserName,
        }),
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to update project');
      }
      toast.success(data.message || 'Project updated');
      cancelEditProject();
      loadProjects();
    } catch (error) {
      toast.error(error.message || 'Failed to update project');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f6fa]">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <main className={`flex-1 transition-all duration-300 w-full ${isSidebarOpen ? 'lg:ml-72' : ''}`}>
          <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-5 sm:py-6 pb-16">
            <section className="bg-white rounded-2xl border border-slate-200 mb-5 shadow-sm overflow-hidden">
              <div className="h-[2px] bg-gradient-to-r from-indigo-500 to-emerald-500" />
              <div className="px-5 py-5 sm:px-6 sm:py-6 flex items-center justify-between gap-4">
                <div>
                  <p className="text-[9px] font-extrabold uppercase tracking-[0.25em] text-indigo-600">Attribute Management</p>
                  <h1 className="mt-1 text-xl sm:text-2xl font-black text-slate-800 tracking-tight leading-none">Projects</h1>
                  <p className="text-[11px] text-slate-400 mt-1.5 font-medium">
                    Allocate project budgets and create project heads with matching sub heads.
                  </p>
                </div>
                <div className="flex-shrink-0 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-center">
                  <p className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider mb-0.5">Projects</p>
                  <p className="text-2xl font-black text-slate-800 leading-none">{projectNames.length}</p>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="h-[2px] bg-gradient-to-r from-indigo-500 to-emerald-500" />
              <form onSubmit={saveProject} className="p-5 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  <label className="grid gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-500">
                    <span className="flex items-center gap-1.5"><BriefcaseIcon className="w-3.5 h-3.5" /> Project Name *</span>
                    <input list="project-names" className={inputCls} value={form.project_name} onChange={(e) => {
                      const value = e.target.value;
                      const existing = projects.find((project) => project.project_name === value);
                      setForm((prev) => ({
                        ...prev,
                        project_name: value,
                        allocated_budget: existing ? existing.allocated_budget : prev.allocated_budget,
                      }));
                    }} placeholder="Select or type project" />
                    <datalist id="project-names">
                      {projectNames.map((name) => <option key={name} value={name} />)}
                    </datalist>
                  </label>
                  <label className="grid gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-500">
                    <span className="flex items-center gap-1.5"><BanknotesIcon className="w-3.5 h-3.5" /> Budget *</span>
                    <input type="number" min="0" step="0.01" className={inputCls} value={form.allocated_budget} onChange={(e) => updateForm('allocated_budget', e.target.value)} placeholder={selectedProject ? String(selectedProject.allocated_budget) : '0.00'} />
                  </label>
                  <label className="grid gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-500">
                    <span className="flex items-center gap-1.5"><TagIcon className="w-3.5 h-3.5" /> Main Head *</span>
                    <input list="project-main-heads" className={inputCls} value={form.main_head_name} onChange={(e) => updateForm('main_head_name', e.target.value)} placeholder="Select or type new" required />
                    <datalist id="project-main-heads">
                      {mainHeads.map((head) => <option key={head} value={head} />)}
                    </datalist>
                  </label>
                  <label className="grid gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-500">
                    <span className="flex items-center gap-1.5"><TagIcon className="w-3.5 h-3.5" /> Sub Head *</span>
                    <input list="project-sub-heads" className={inputCls} value={form.sub_main_name} onChange={(e) => updateForm('sub_main_name', e.target.value)} placeholder="Select or type new" required />
                    <datalist id="project-sub-heads">
                      {subHeads.map((subHead) => <option key={subHead} value={subHead} />)}
                    </datalist>
                  </label>
                  <div className="flex items-end">
                    <button
                      type="submit"
                      disabled={isSaving}
                      className="w-full h-10 inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 text-[11px] font-extrabold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <CheckCircleIcon className="w-3.5 h-3.5" />
                      {isSaving ? 'Saving...' : 'Save Head'}
                    </button>
                  </div>
                </div>
                <p className="mt-3 flex items-center gap-1.5 text-[11px] font-medium text-slate-400">
                  <UserIcon className="w-3.5 h-3.5" />
                  Main head and sub head are saved together. Existing project heads are shown read-only in the table.
                </p>
              </form>
            </section>

            {editingProject && (
              <section className="mt-5 bg-white rounded-2xl border border-brand-200 shadow-sm overflow-hidden">
                <div className="h-[2px] bg-brand-600" />
                <form onSubmit={updateProject} className="p-5 sm:p-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <p className="text-[9px] font-extrabold uppercase tracking-[0.25em] text-brand-600">Edit Project</p>
                      <h2 className="mt-1 text-lg font-black text-slate-800">Update project name and budget</h2>
                      <p className="mt-1 text-[11px] text-slate-500">This updates all rows and heads under this project.</p>
                    </div>
                    <button type="button" onClick={cancelEditProject} className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px_auto] gap-4">
                    <label className="grid gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-500">
                      Project Name *
                      <input className={inputCls} value={editForm.project_name} onChange={(e) => setEditForm((prev) => ({ ...prev, project_name: e.target.value }))} />
                    </label>
                    <label className="grid gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-500">
                      Budget *
                      <input type="number" min="0" step="0.01" className={inputCls} value={editForm.allocated_budget} onChange={(e) => setEditForm((prev) => ({ ...prev, allocated_budget: e.target.value }))} />
                    </label>
                    <div className="flex items-end">
                      <button type="submit" disabled={isUpdating} className="h-10 rounded-lg bg-brand-600 px-5 text-[11px] font-extrabold text-white disabled:opacity-60">
                        {isUpdating ? 'Updating...' : 'Update'}
                      </button>
                    </div>
                  </div>
                </form>
              </section>
            )}

            <section className="mt-5 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Project</th>
                      <th className="px-4 py-3">Budget</th>
                      <th className="px-4 py-3">Main Head</th>
                      <th className="px-4 py-3">Sub Head</th>
                      <th className="px-4 py-3">Created By</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Updated By</th>
                      <th className="px-4 py-3">Updated At</th>
                      <th className="px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {projects.length === 0 && (
                      <tr>
                        <td colSpan="9" className="px-4 py-8 text-center text-slate-400">No projects found.</td>
                      </tr>
                    )}
                    {projects.map((project) => (
                      <tr key={project.project_id}>
                        <td className="px-4 py-3 font-bold text-slate-800">{project.project_name}</td>
                        <td className="px-4 py-3 text-slate-600">₹{Number(project.allocated_budget || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-slate-600">{project.main_head_name || '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{project.sub_main_name || '-'}</td>
                        <td className="px-4 py-3 text-slate-500">{project.created_by || '-'}</td>
                        <td className="px-4 py-3 text-slate-500">{project.created_date || '-'}</td>
                        <td className="px-4 py-3 text-slate-500">{project.updated_by || '-'}</td>
                        <td className="px-4 py-3 text-slate-500">{project.updated_date || project.updated_at || '-'}</td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => startEditProject(project)}
                            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-brand-200 bg-brand-50 px-3 text-[11px] font-bold text-brand-700 hover:bg-brand-100"
                          >
                            <PencilSquareIcon className="h-3.5 w-3.5" />
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </main>
      </div>
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
    </div>
  );
};

export default Projects;
